import React, {useState} from "react";
import {useTitansByDMRealtime} from "../../hooks/useFirestore";
import {useAuth} from "../../hooks/useAuth";
import {FirestoreTitan} from "../../services/firebase/firestore";
import {TitanService} from "../../services/api/titanService";
import {useNotifications} from "../../components/NotificationSystem";
import {useSessionsByTitan} from "../../hooks/useFirestore";
import {getDisplayDate} from "../../utils/dateUtils";
import "../../styles/env.css";
import ConfirmDeleteDialog from "../../components/ConfirmDeleteDialog";
import RichTextEditor from "../../components/RichText/RichTextEditor";
import RichTextRenderer from "../../components/RichText/RichTextRenderer";

const TitanManagement: React.FC = () => {
  const {user} = useAuth(); // Only for user.uid, role checking handled by AuthGuard
  const {
    data: firestoreTitans,
    loading: titansLoading,
    error: titansError,
  } = useTitansByDMRealtime(user?.uid || "");
  const {addNotification} = useNotifications();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [links, setLinks] = useState<string[]>([]);
  const [linkInput, setLinkInput] = useState("");
  const [expandedTitan] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  // Add state for editing a link
  const [editingLinkIdx, setEditingLinkIdx] = useState<number | null>(null);
  const [editingLinkValue, setEditingLinkValue] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteTitanId, setPendingDeleteTitanId] = useState<
    string | null
  >(null);
  const [pendingDeleteLinkIdx, setPendingDeleteLinkIdx] = useState<
    number | null
  >(null);
  const [deleteDialogMessage, setDeleteDialogMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);

  const handleCancel = () => {
    setName("");
    setDescription("");
    setLinks([]);
    setLinkInput("");
    setEditId(null);
    setOpen(false);
  };

  const handleCreateOrEditTitan = async () => {
    // Strip HTML tags for validation
    const stripHtml = (html: string) => {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    };

    if (!name.trim() || !stripHtml(description).trim()) {
      addNotification({
        type: "error",
        title: "Validation Error",
        message: "Name and description are required fields.",
      });
      return;
    }

    setIsSubmitting(true);
    setOperationError(null);

    try {
      if (editId) {
        await TitanService.updateTitan({
          titanId: editId,
          name,
          description,
          links,
        });
        addNotification({
          type: "success",
          title: "Titan Updated",
          message: `Successfully updated "${name}"`,
        });
      } else {
        await TitanService.createTitan({
          name,
          description,
          links,
          levelRange: "Level 1-20",
          specialties: [],
        });
        addNotification({
          type: "success",
          title: "Titan Created",
          message: `Successfully created "${name}"`,
        });
      }
      handleCancel();
    } catch (error: any) {
      const errorMessage =
        error.message || "Failed to save titan. Please try again.";
      setOperationError(errorMessage);
      addNotification({
        type: "error",
        title: "Save Failed",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTitan = (titan: FirestoreTitan) => {
    setName(titan.name);
    setDescription(titan.description);
    setLinks(titan.links || []);
    setEditId(titan.id || "");
    setOpen(true);
  };

  const handleDeleteTitan = (id: string) => {
    setPendingDeleteTitanId(id);
    setDeleteDialogMessage("Are you sure you want to delete this titan?");
    setDeleteDialogOpen(true);
  };

  const handleDeleteLink = (idx: number) => {
    setPendingDeleteLinkIdx(idx);
    setDeleteDialogMessage("Are you sure you want to delete this link?");
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteTitanId) {
      try {
        await TitanService.deleteTitan({titanId: pendingDeleteTitanId});
        addNotification({
          type: "success",
          title: "Titan Deleted",
          message: "Titan has been successfully deleted.",
        });
        setPendingDeleteTitanId(null);
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to delete titan. Please try again.";
        addNotification({
          type: "error",
          title: "Delete Failed",
          message: errorMessage,
        });
      }
    } else if (pendingDeleteLinkIdx !== null) {
      setLinks(links.filter((_, i) => i !== pendingDeleteLinkIdx));
      setPendingDeleteLinkIdx(null);
    }
    setDeleteDialogOpen(false);
    setDeleteDialogMessage("");
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setPendingDeleteTitanId(null);
    setPendingDeleteLinkIdx(null);
    setDeleteDialogMessage("");
  };

  // Show loading state
  if (titansLoading) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Titan Management</h2>
        <div className="env-session-loading-center">Loading titans...</div>
      </div>
    );
  }

  // Show error state
  if (titansError) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Titan Management</h2>
        <div className="env-session-error-center">
          Error loading titans: {titansError}
        </div>
      </div>
    );
  }

  return (
    <div className="env-page-container">
      <h2 className="env-page-title">Titan Management</h2>
      <section className="env-section">
        <div className="env-section-header">
          <button
            className="btn btn-tertiary"
            onClick={() => {
              setOpen(true);
              setEditId(null);
              setName("");
              setDescription("");
              setLinks([]);
              setLinkInput("");
            }}
          >
            + Create new Titan
          </button>
        </div>
        {open && (
          <div className="env-card-medium env-titan-create-form">
            <div className="env-card-title env-session-edit-header">
              {editId && (
                <button
                  onClick={handleCancel}
                  className="btn btn-secondary btn-small"
                  aria-label="Back"
                >
                  Back
                </button>
              )}
              {editId ? "EDIT TITAN" : "CREATE TITAN"}
            </div>
            {operationError && (
              <div className="env-session-operation-error">
                {operationError}
              </div>
            )}
            <div className="env-form-group">
              <label htmlFor="titan-name">Name *</label>
              <input
                id="titan-name"
                className={`env-input ${!name.trim() && isSubmitting ? "env-input-error" : ""}`}
                placeholder="Enter titan name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {!name.trim() && isSubmitting && (
                <div className="env-form-validation-error">
                  Name is required
                </div>
              )}
            </div>
            <div className="env-form-group">
              <label htmlFor="titan-description">Description *</label>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Enter titan description..."
                minHeight="150px"
                maxHeight="400px"
              />
              {(() => {
                const stripHtml = (html: string) => {
                  const tmp = document.createElement("div");
                  tmp.innerHTML = html;
                  return tmp.textContent || tmp.innerText || "";
                };
                return !stripHtml(description).trim() && isSubmitting ? (
                  <div className="env-form-validation-error">
                    Description is required
                  </div>
                ) : null;
              })()}
            </div>
            {/* Links Section */}
            <div className="env-form-group">
              <div className="env-form-label-row">
                <label htmlFor="titan-links">Links</label>
              </div>
              <div className="env-link-input-wrapper">
                <input
                  id="titan-links"
                  className="env-input env-link-input"
                  type="url"
                  placeholder="Type or paste any link"
                  value={linkInput}
                  onChange={(e) => setLinkInput(e.target.value)}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("Text");
                    let url = pasted.trim();
                    if (url && !/^https?:\/\//i.test(url)) {
                      url = "https://" + url;
                    }
                    if (url && /^https?:\/\//i.test(url)) {
                      setLinks([...links, url]);
                      setLinkInput("");
                      e.preventDefault();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      let url = linkInput.trim();
                      if (url && !/^https?:\/\//i.test(url)) {
                        url = "https://" + url;
                      }
                      if (url && /^https?:\/\//i.test(url)) {
                        setLinks([...links, url]);
                        setLinkInput("");
                        e.preventDefault();
                      }
                    }
                  }}
                />
                <span className="env-link-hint">press ⏎ to add the link</span>
              </div>
              <ul className="env-link-list">
                {links.map((link, idx) => (
                  <li key={link + idx} className="env-link-item">
                    <button
                      type="button"
                      className="btn btn-tertiary btn-small env-link-action-btn"
                      aria-label="Edit link"
                      onClick={() => {
                        setEditingLinkIdx(idx);
                        setEditingLinkValue(link.replace(/^https?:\/\//i, ""));
                      }}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 17L7 16L16 7C16.5523 6.44772 16.5523 5.55228 16 5C15.4477 4.44772 14.5523 4.44772 14 5L5 14L3 17Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="btn btn-destructive btn-small env-link-action-btn"
                      aria-label="Remove link"
                      onClick={() => handleDeleteLink(idx)}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 7V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V7M4 7H16M9 10V13M11 10V13M8 7V5C8 4.44772 8.44772 4 9 4H11C11.5523 4 12 4.44772 12 5V7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    {editingLinkIdx === idx ? (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          let url = editingLinkValue.trim();
                          if (url && !/^https?:\/\//i.test(url)) {
                            url = "https://" + url;
                          }
                          if (url && /^https?:\/\//i.test(url)) {
                            setLinks(
                              links.map((l, i) => (i === idx ? url : l))
                            );
                            setEditingLinkIdx(null);
                            setEditingLinkValue("");
                          }
                        }}
                        className="env-link-edit-form"
                      >
                        <input
                          type="text"
                          value={editingLinkValue}
                          onChange={(e) => setEditingLinkValue(e.target.value)}
                          className="env-input env-link-edit-input"
                          autoFocus
                          onBlur={() => {
                            setEditingLinkIdx(null);
                            setEditingLinkValue("");
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              setEditingLinkIdx(null);
                              setEditingLinkValue("");
                            }
                          }}
                        />
                        <button type="submit" className="env-hidden" />
                      </form>
                    ) : (
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="env-link-display"
                      >
                        {link}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="env-form-actions">
              <button
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateOrEditTitan}
                disabled={isSubmitting || !name.trim() || !description.trim()}
              >
                {isSubmitting ? (
                  <span className="env-loading-text">
                    <div className="loading-spinner env-loading-spinner-small"></div>
                    Saving...
                  </span>
                ) : editId ? (
                  "Save Changes"
                ) : (
                  "Create Titan"
                )}
              </button>
            </div>
          </div>
        )}
        {/* Titan List */}
        <div className="env-item-list">
          {firestoreTitans.length === 0 && !open ? (
            <div className="env-empty-state">No titans created yet.</div>
          ) : (
            firestoreTitans
              .filter((titan) => titan.id !== editId) // Hide the titan being edited
              .map((titan) => (
                <div key={titan.id} className="env-card-medium">
                  <div className="env-session-header">
                    <h3>{titan.name}</h3>
                    <div className="env-session-actions">
                      <button
                        className="btn btn-secondary btn-small"
                        onClick={() => handleEditTitan(titan)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-destructive btn-small"
                        onClick={() => handleDeleteTitan(titan.id || "")}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="env-titan-description">
                    {expandedTitan === titan.id ? (
                      <RichTextRenderer content={titan.description} />
                    ) : (
                      (() => {
                        // Strip HTML for length check and preview
                        const stripHtml = (html: string) => {
                          const tmp = document.createElement("div");
                          tmp.innerHTML = html;
                          return tmp.textContent || tmp.innerText || "";
                        };
                        const plainText = stripHtml(titan.description);
                        const isLong = plainText.length > 180;
                        return isLong ? (
                          <div>
                            <div className="env-titan-description-preview">
                              {plainText.slice(0, 180)}...
                            </div>
                          </div>
                        ) : (
                          <RichTextRenderer content={titan.description} />
                        );
                      })()
                    )}
                  </div>
                  {titan.links && titan.links.length > 0 && (
                    <div className="env-titan-links">
                      {titan.links.map((link, idx) => (
                        <a
                          key={link + idx}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="env-link-display"
                        >
                          {link}
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="env-titan-divider" />
                  <TitanSessions titanId={titan.id || ""} />
                </div>
              ))
          )}
        </div>
      </section>
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        message={deleteDialogMessage}
      />
    </div>
  );
};

// TitanSessions component to display sessions for a specific titan
interface TitanSessionsProps {
  titanId: string;
}

const TitanSessions: React.FC<TitanSessionsProps> = ({titanId}) => {
  const {
    data: sessions,
    loading: sessionsLoading,
    error: sessionsError,
  } = useSessionsByTitan(titanId);

  if (sessionsLoading) {
    return (
      <div className="env-titan-sessions">
        <div className="env-titan-sessions-header">Sessions on Titan</div>
        <div className="env-titan-sessions-loading">Loading sessions...</div>
      </div>
    );
  }

  if (sessionsError) {
    return (
      <div className="env-titan-sessions">
        <div className="env-titan-sessions-header">Sessions on Titan</div>
        <div className="env-titan-sessions-error">Error loading sessions</div>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="env-titan-sessions">
        <div className="env-titan-sessions-header">Sessions on Titan</div>
        <div className="env-titan-sessions-empty">
          No sessions on this titan yet
        </div>
      </div>
    );
  }

  return (
    <div className="env-titan-sessions">
      <div className="env-titan-sessions-header">
        Sessions on Titan ({sessions.length})
      </div>
      <div className="env-titan-sessions-list">
        {sessions.slice(0, 3).map((session) => (
          <div key={session.id} className="env-titan-session-item">
            <div className="env-titan-session-name">{session.name}</div>
            <div className="env-titan-session-details">
              {session.status} • {getDisplayDate(session.date) || "Date TBD"}
            </div>
          </div>
        ))}
        {sessions.length > 3 && (
          <div className="env-titan-sessions-more">
            +{sessions.length - 3} more sessions
          </div>
        )}
      </div>
    </div>
  );
};

export default TitanManagement;
